// Quick-action stat row for the Lineup tab hero card: pending waiver claims,
// freezes remaining, 5x Play status, and how scoring works. Each cell is an
// entry point, not a page: it opens a sheet, per the brief. Rendered as a row
// meant to sit inside the hero card, not as a standalone chip strip.
//
// Scoring sits here because this is where it is needed. A manager reads the
// rules while choosing a captain or deciding whether to spend the 5x, not by
// navigating to a rules page first.
import React, { useState } from 'react';
import { Gavel, Snowflake, Zap, Calculator } from 'lucide-react';
import BottomSheet from '../sheets/BottomSheet';
import ScoringSystemModal from '../ScoringSystemModal';
import { useWaiverBids } from '../../hooks/useWaiverBids';
import { useBiddingWindow } from '../../hooks/useBiddingWindow';
import { FIRST_FREE_AGENCY_WEEK } from '../../utils/season';
import { useTeams } from '../../hooks/useTeams';
import ClaimRow, { sortByProcessingOrder, claimsWithoutRoom } from '../teams/ClaimRow';

function StatCell({ icon: Icon, label, value, onClick, tour }) {
  return (
    <button
      data-tour={tour}
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
        padding: '10px 4px', minHeight: 56, flex: 1,
        border: 'none', background: 'none',
        cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
      }}
    >
      <Icon size={16} color="var(--color-brand)" />
      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-strong)' }}>{value}</span>
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

// What to say when a manager has no claims: why, not just that.
function emptyBidsText(biddingWindow) {
  switch (biddingWindow.state) {
    case 'locked':
      return 'Free agency is paused.';
    case 'before':
      return `Waivers open at 10am ET on the Sunday after week ${FIRST_FREE_AGENCY_WEEK}.`;
    case 'waiting':
      return `Waivers open at 10am ET Sunday, after week ${biddingWindow.afterWeek}.`;
    case 'resolving':
      return 'Bidding has closed. Bids resolve at 6am ET.';
    case 'closed':
      return 'Waivers are closed for the season.';
    default:
      return 'Bidding is open. You have no claims in this week.';
  }
}

export default function QuickActionChips({ leagueId, memberId, freezesRemaining, hasTripPlay, tripPlayUsedWeek, emptySlots = 0, autoOpenWaivers = false }) {
  const { teams } = useTeams();
  const { bids, pending, cancelBid, reorderBids, loading: bidsLoading } = useWaiverBids({ leagueId, memberId });
  // Listed in the order the 6am run will process them, so the list reads as
  // the priority it is. Results include skipped claims with their reason: a
  // claim that silently vanished from Pending was the other half of the
  // confusion here.
  const pendingOrdered = sortByProcessingOrder(pending);

  // Moves the claim at index by one place and sends the whole reordered list.
  // reorder_bids refuses anything short of the full pending set, so the
  // up/down buttons always resend it rather than a single pair of ids.
  const moveClaim = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= pendingOrdered.length) return;
    const ids = pendingOrdered.map((b) => b.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderBids(ids);
  };
  const withoutRoom = claimsWithoutRoom(pendingOrdered, emptySlots);
  const noDropCount = pendingOrdered.filter((b) => !b.drop_team_id).length;
  const resolved = bids
    .filter((b) => ['won', 'lost', 'skipped'].includes(b.status))
    .sort((a, b) => new Date(b.resolved_at || b.created_at) - new Date(a.resolved_at || a.created_at));
  // "No claims this week" reads as "you have not bid yet" in a week where
  // nobody can bid at all. Say which it is, in the same words the Teams tab
  // uses, from the same hook, so the two cannot disagree.
  const biddingWindow = useBiddingWindow();
  const [openSheet, setOpenSheet] = useState(autoOpenWaivers ? 'bids' : null); // 'bids' | 'freezes' | 'tripplay' | null
  const [showScoring, setShowScoring] = useState(false);

  const tripPlayLabel = hasTripPlay
    ? 'Available'
    : tripPlayUsedWeek
    ? `Used Wk ${tripPlayUsedWeek}`
    : 'Used';

  return (
    <>
      <div style={{ display: 'flex' }}>
        <StatCell icon={Gavel} label="Waivers" value={pending.length} onClick={() => setOpenSheet('bids')} />
        <div style={{ width: 1, backgroundColor: 'var(--color-bg-subtle)' }} />
        <StatCell icon={Snowflake} label="Freezes" value={`${freezesRemaining}/3`} onClick={() => setOpenSheet('freezes')} tour="lineup-freezes" />
        <div style={{ width: 1, backgroundColor: 'var(--color-bg-subtle)' }} />
        <StatCell icon={Zap} label="5x Play" value={tripPlayLabel} onClick={() => setOpenSheet('tripplay')} tour="lineup-tripplay" />
        <div style={{ width: 1, backgroundColor: 'var(--color-bg-subtle)' }} />
        <StatCell icon={Calculator} label="Scoring" value="Rules" onClick={() => setShowScoring(true)} />
      </div>

      {showScoring && <ScoringSystemModal onClose={() => setShowScoring(false)} />}

      <BottomSheet open={openSheet === 'bids'} onClose={() => setOpenSheet(null)} title="Waiver claims">
        {bidsLoading ? (
          <p style={{ fontSize: 15, color: 'var(--color-text-muted)' }}>Loading...</p>
        ) : (
          <>
            {pending.length === 0 && resolved.length === 0 && (
              <p style={{ fontSize: 15, color: 'var(--color-text-muted)' }}>{emptyBidsText(biddingWindow)}</p>
            )}

            {pendingOrdered.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Pending</div>
                <p style={{ fontSize: 12, color: 'var(--color-text-faint)', margin: '2px 0 4px' }}>
                  Processed in this order. Use the arrows to change it. Bids resolve at 6am ET Tuesday.
                </p>
                {withoutRoom.size > 0 && (
                  <div style={{ backgroundColor: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-fill)', borderRadius: 12, padding: '10px 14px', margin: '8px 0' }}>
                    <p style={{ fontSize: 13, color: 'var(--color-warning-text)', margin: 0, lineHeight: 1.5 }}>
                      You have {noDropCount} claims without a drop and {emptySlots} empty {emptySlots === 1 ? 'slot' : 'slots'}.
                      {' '}If they all win, the lowest {withoutRoom.size === 1 ? 'one is' : `${withoutRoom.size} are`} skipped.
                      {' '}To keep {withoutRoom.size === 1 ? 'it' : 'them'} in play, open the team on the Teams tab and update the bid with a team to drop.
                    </p>
                  </div>
                )}
                {pendingOrdered.map((bid, i) => (
                  <ClaimRow
                    key={bid.id}
                    bid={bid}
                    teams={teams}
                    rank={i + 1}
                    onCancel={cancelBid}
                    noRoomIfAllWin={withoutRoom.has(bid.id)}
                    reorderable={pendingOrdered.length > 1}
                    onMoveUp={i > 0 ? () => moveClaim(i, -1) : null}
                    onMoveDown={i < pendingOrdered.length - 1 ? () => moveClaim(i, 1) : null}
                  />
                ))}
              </div>
            )}

            {resolved.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Results</div>
                {resolved.map((bid) => (
                  <ClaimRow key={bid.id} bid={bid} teams={teams} />
                ))}
              </div>
            )}
          </>
        )}
      </BottomSheet>

      <BottomSheet open={openSheet === 'freezes'} onClose={() => setOpenSheet(null)} title="Freeze plays">
        <p style={{ fontSize: 15, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          You have <strong>{freezesRemaining} of 3</strong> freezes remaining this season.
          A freeze locks in a team's live score once its game reaches the second half.
          It cannot be undone.
        </p>
      </BottomSheet>

      <BottomSheet open={openSheet === 'tripplay'} onClose={() => setOpenSheet(null)} title="5x Play">
        <p style={{ fontSize: 15, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          {hasTripPlay
            ? 'Your 5x Play is available. It goes on your captain for the week you use it, and that team scores five times its points instead of double, win or lose.'
            : `You used your 5x Play in week ${tripPlayUsedWeek ?? '—'}. It is once per season.`}
        </p>
      </BottomSheet>
    </>
  );
}
